import { CardContent, CardHeading } from '@/components/ui'
import type { Transaction } from '@/modules/projects'
import { countsAsMoneyFlow } from '@/modules/projects/utils'
import {
  categoryColors,
  chartColors,
  getProjectPath,
  numberToCurrency,
  percentFromRate,
} from '@/utils'
import { OverviewBarRow } from './OverviewBarRow'
import { OverviewViewLink } from './OverviewViewLink'
import { ProcessingCard } from './ProcessingCard'

interface OverviewTopCategoriesProps {
  slug: string
  transactions: Transaction[]
}

// Reuse the app's canonical per-category colors so a category reads the same
// hue here as it does in the Categories insight.
const categoryColor = (category: string): string =>
  (categoryColors as Record<string, string>)[category] ?? chartColors.gray

// Top five spend categories by outflow. Derived from the transactions' own
// category field (no generation), with a link to the full Categories view.
export const OverviewTopCategories = ({
  slug,
  transactions,
}: OverviewTopCategoriesProps) => {
  const totals = new Map<string, number>()
  for (const transaction of transactions) {
    if (transaction.amount >= 0) continue
    // Sweeps between the customer's own buckets and securities trades are not spending.
    if (!countsAsMoneyFlow(transaction)) continue
    // Skip uncategorized so the card shows real categories or a clean empty
    // state, never one giant "Uncategorized" bar.
    const category = transaction.category
    if (!category) continue
    totals.set(
      category,
      (totals.get(category) ?? 0) + Math.abs(transaction.amount),
    )
  }
  const totalSpend = Array.from(totals.values()).reduce(
    (sum, amount) => sum + amount,
    0,
  )
  const rows = Array.from(totals.entries())
    .map(([category, amount]) => ({ category, amount }))
    .sort((first, second) => second.amount - first.amount)
    .slice(0, 5)
  const max = rows[0]?.amount ?? 0

  return (
    <ProcessingCard>
      <CardHeading
        title="Top categories"
        actions={
          <OverviewViewLink
            to={`${getProjectPath({ slug })}/insights/categories`}
          />
        }
      />
      <CardContent>
        {rows.length === 0 ? (
          <div className="flex min-h-[120px] items-center justify-center text-center text-sm text-muted-foreground">
            No categorized spend yet.
          </div>
        ) : (
          <div className="space-y-1">
            {rows.map((row) => (
              <OverviewBarRow
                key={row.category}
                label={row.category}
                color={categoryColor(row.category)}
                fraction={max > 0 ? row.amount / max : 0}
                value={numberToCurrency(row.amount, {
                  maximumFractionDigits: 0,
                })}
                valueMuted={
                  totalSpend > 0
                    ? percentFromRate(row.amount / totalSpend)
                    : undefined
                }
              />
            ))}
          </div>
        )}
      </CardContent>
    </ProcessingCard>
  )
}
